import { grey } from '@mui/material/colors';
import { LayerType } from '@pn/core/domain/layer';
import { type AnyColor, type Colord, colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import namesPlugin from 'colord/plugins/names';
import { isArray, isNil, isString } from 'lodash-es';

/**
 * Adds CSS color names (incl. `transparent`) and contrast ratios. Import
 * `colord` from this module rather than from the package, so these plugins are
 * always applied.
 */
extend([namesPlugin, a11yPlugin]);

export { colord };

/**
 * Uppercase `#RRGGBB` with alpha dropped, the format saved styles use.
 */
export function toHex(color: AnyColor | Colord): string {
  return colord(color).alpha(1).toHex().toUpperCase();
}

export function addAlpha(color: string, alpha: number): string {
  return colord(color).alpha(alpha).toRgbString();
}

export function isValidColorString(arg: string): boolean {
  return arg.startsWith('#') || arg.startsWith('rgb') || arg.startsWith('hsl');
}

export function isTransparent(color: string): boolean {
  return colord(color).alpha() === 0;
}

export function getStepColor(params: {
  index: number;
  total: number;
  min: string;
  max: string;
}): string {
  const { index, total, min, max } = params;

  if (index < 0 || index > total || total <= 0) {
    throw new Error('Invalid index or total');
  }

  if (total === 1) {
    return toHex(min); // to prevent NaN in ratio calculation
  }

  const minColor = colord(min).toRgb();
  const maxColor = colord(max).toRgb();

  /**
   * Calculate the ratio of index to total
   * Note: we subtract 1 from total because index is zero-based
   */
  const ratio = index / (total - 1);

  /**
   * Mix the two colors linearly in RGB space, with the ratio determining the
   * weight between them: ratio = 0 will result in minColor, ratio = 1 will
   * result in maxColor.
   */
  const mix = (from: number, to: number) => from + (to - from) * ratio;

  return toHex({
    r: mix(minColor.r, maxColor.r),
    g: mix(minColor.g, maxColor.g),
    b: mix(minColor.b, maxColor.b),
  });
}

/**
 * Generates a selection color from the base color.
 */
export function generateSelectionColor(
  baseColor: string | undefined,
  layerType: LayerType
): string {
  const lightness = layerType === LayerType.Polygon ? 65 : 50;
  const saturation = layerType === LayerType.Polygon ? 75 : 100;

  const color = colord(baseColor ?? '#000').toHsl();

  if (isNil(baseColor) || color.l <= 20 || color.l >= 80) {
    return toHex({ h: 340, s: 100, l: lightness }); // default pink selection color
  }

  const hue = getDistinctHue(color.h);
  let output = colord({ h: hue, s: saturation, l: lightness });

  if (layerType !== LayerType.Polygon && output.contrast('white') < 6) {
    output = colord({
      h: hue,
      s: saturation,
      l: 50 - Math.abs(output.contrast('white') - 6) * 4,
    });
  }

  return toHex(output);
}

/**
 * Takes an existing color expression and generates a selection version of it.
 */
export function generateSelectionExpression(
  expression: unknown[],
  layerType: LayerType
): unknown[] {
  if (expression[0] === 'step') {
    return expression.map((value, index) => {
      if (index < 2 || index % 2 === 1) {
        return value;
      } else if (isString(value)) {
        return generateSelectionColor(value, layerType);
      } else {
        return value;
      }
    });
  } else if (expression[0] === 'match') {
    return expression.map((value, index) => {
      const isWithinFirstTwoOrEven = index < 3 || index % 2 === 0;
      const isLast = index === expression.length - 1;

      if (isWithinFirstTwoOrEven && !isLast) {
        return value;
      } else {
        return generateSelectionColor(value as string, layerType);
      }
    });
  } else {
    throw new Error(`Unsupported expression type: ${expression[0]}`);
  }
}

/**
 * Generates a distinct hue from the base hue.
 * We start with a 180 degrees rotation and then exclude all undesirable hues.
 */
function getDistinctHue(baseHue: number): number {
  let outputHue = (baseHue + 180) % 360;

  const avoidRange = [0, 150];
  const avoidSpan = avoidRange[1] - avoidRange[0];

  if (
    outputHue >= avoidRange[0] &&
    outputHue <= avoidRange[1] - avoidSpan / 2
  ) {
    outputHue = avoidRange[0];
  } else if (
    outputHue >= avoidRange[1] - avoidSpan / 2 &&
    outputHue <= avoidRange[1]
  ) {
    outputHue = avoidRange[1];
  }

  return outputHue;
}

export function blendColors(
  foreground: string,
  background: string,
  alpha: number
): string {
  const fg = colord(foreground).toRgb();
  const bg = colord(background).toRgb();

  return toHex({
    r: Math.round((1 - alpha) * bg.r + alpha * fg.r),
    g: Math.round((1 - alpha) * bg.g + alpha * fg.g),
    b: Math.round((1 - alpha) * bg.b + alpha * fg.b),
  });
}

/**
 * A random vivid color, always the same one for a given seed. Light colors
 * read better on dark backgrounds.
 */
export function generateRandomColor(
  params: { seed?: number; light?: boolean } = {}
): string {
  const { seed, light = false } = params;
  const random = isNil(seed) ? Math.random : seededRandom(seed);

  return toHex({
    h: Math.round(random() * 360),
    s: 65 + Math.round(random() * 25),
    l: (light ? 65 : 45) + Math.round(random() * 10),
  });
}

/* Mulberry32, a small PRNG that returns floats in [0, 1). */
function seededRandom(seed: number): () => number {
  let state = seed % 2 ** 32;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Lowers the saturation by `amount` of its current value (0.5 halves it).
 */
export function desaturateColor(color: string, amount: number): string {
  const hsl = colord(color).toHsl();
  return toHex({ ...hsl, s: hsl.s * (1 - amount) });
}

const getRainbowGradient = (type: string, angle: number) => `
${type}-gradient(
  ${angle}deg,
  rgba(255, 0, 0, 1) 0%,
  rgba(255, 154, 0, 1) 10%,
  rgba(208, 222, 33, 1) 20%,
  rgba(79, 220, 74, 1) 30%,
  rgba(63, 218, 216, 1) 40%,
  rgba(47, 201, 226, 1) 50%,
  rgba(28, 127, 238, 1) 60%,
  rgba(95, 21, 242, 1) 70%,
  rgba(186, 12, 248, 1) 80%,
  rgba(251, 7, 217, 1) 90%,
  rgba(255, 0, 0, 1) 100%
)
`;

const STRIPES_GRADIENT = `
repeating-linear-gradient(
  45deg,
  rgba(255, 255, 255, 0.5),
  rgba(255, 255, 255, 0.5) 2px,
  rgba(0, 0, 0, 0.25) 2px,
  rgba(0, 0, 0, 0.25) 4px
)
`;

export function generateIconBackground(color: unknown): string {
  if (isString(color) && isTransparent(color)) {
    return STRIPES_GRADIENT;
  } else if (isString(color)) {
    return toHex(color);
  } else if (isArray(color)) {
    return getRainbowGradient('linear', 45);
  } else {
    return grey[500]; // should never happen
  }
}

/**
 * Generate border color based on icon color and background.
 * Returns undefined if no border is needed based on contrast ratio.
 */
export function getIconBorder(color: unknown, bg: string): string | undefined {
  if (!isString(color) || isTransparent(color)) return undefined;

  const contrast = colord(color).contrast(bg);
  const contrastThreshold = 1.5;

  if (contrast < contrastThreshold) {
    const hsl = colord(color).toHsl();
    const clampedLightness = colord(bg).isLight()
      ? Math.min(hsl.l - 20, 75)
      : Math.max(hsl.l + 20, 40);
    return toHex({ ...hsl, l: clampedLightness });
  } else {
    return undefined;
  }
}
